/* @jsx h */
import { defineComponent, ref, onMounted, h, computed } from "vue";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { TableStatus } from "@lemmonhq/types";
import { Loading, LemmonModal } from "@app/vue/components/index";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { sortTables } from "@lemmonhq/orders";
import { tablesQueryKeys } from "./data";

interface ChangeTableModalProps {
  currentTable: TableStatus;
  onClose: () => void;
  onSuccess: () => void;
}

export const ChangeTableModal = defineComponent<ChangeTableModalProps>({
  name: "ChangeTableModal",
  props: ["currentTable", "onClose", "onSuccess"],
  setup(props) {
    const trpc = useTrpc();
    const selectedTableId = ref<number | null>(null);
    const forceChange = ref(false);
    const toast = useToast();

    const tablesQuery = useQuery({
      queryKey: tablesQueryKeys.status(),
      queryFn: () => trpc.tables.list.query(),
    });

    const tables = computed<TableStatus[]>(() =>
      (tablesQuery.data.value || []).filter(
        (table) => table.table.id !== props.currentTable.table.id,
      ),
    );

    const changeTableMutation = useMutation({
      mutationFn: (input: {
        table_id: number;
        target_table_id: number;
        force: boolean;
      }) => trpc.tables.change.mutate(input),
    });

    const handleSubmit = async () => {
      if (!selectedTableId.value) return;
      try {
        const result = await changeTableMutation.mutateAsync({
          table_id: props.currentTable.table.id,
          target_table_id: selectedTableId.value,
          force: forceChange.value,
        });

        if ((result as any)?.force && !forceChange.value) {
          forceChange.value = true;
          return;
        }

        props.onSuccess();
      } catch (error) {
        console.error("Failed to change table:", error);
        toast.showToast("Failed to change table", { type: "error" });
      } finally {
      }
    };
    onMounted(() => {
      const element = document.activeElement;
      if (element && element instanceof HTMLElement) element.blur();
    });

    return () => (
      <LemmonModal
        title="Change Table"
        onClose={props.onClose}
        renderButtons={() => (
          <div class="p-4 border-t ">
            <div class="flex justify-between gap-2">
              <button onClick={props.onClose} class="btn btn-neutral">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                class="btn btn-primary"
                disabled={
                  !selectedTableId.value || changeTableMutation.isPending.value
                }
              >
                {forceChange.value ? "Force Change" : "Change Table"}
              </button>
            </div>
          </div>
        )}
      >
        <div class="p-6">
          {tablesQuery.isFetching.value ? (
            <div class="flex justify-center">
              <Loading />
            </div>
          ) : (
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium  mb-2">
                  Select Target Table
                </label>
                <select
                  class="select select-bordered w-full bg-gray-900 text-accent"
                  value={selectedTableId.value || ""}
                  onChange={(e) => {
                    selectedTableId.value = Number(
                      (e.target as HTMLSelectElement).value,
                    );
                    forceChange.value = false; // Reset force flag when table changes
                  }}
                >
                  <option value="">Select a table...</option>
                  {sortTables(tables.value).map((table) => (
                    <option key={table.table.id} value={table.table.id}>
                      {table.table.name}
                      {table.table.is_busy ? " | Busy" : ""}
                    </option>
                  ))}
                </select>
              </div>
              {forceChange.value && (
                <div class="alert alert-warning">
                  <span class="material-symbols-outlined">error</span>
                  <span>
                    Target table has existing orders. Proceeding will merge the
                    orders.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </LemmonModal>
    );
  },
});
